import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ensureAudioContextResumed } from '@/audio/audioProcessor.ts';
import { capabilities } from '@/capabilities.ts';
import { sendToNative } from '@/bridge/embeddedBridge.ts';
import styles from './AudioWorkspace.module.css';

export interface StagedAudio {
  audioBuffer: AudioBuffer | null;
  peaks: Float32Array;
  rawData: Uint8Array;
  duration: number;
  label: string;
  mimeType: string;
  /** TTS-specific: token cost for generation history */
  tokenCost?: number;
  /** Embedded recording: clip already in Unity's SQLite */
  clipId?: number;
}

interface Props {
  staged: StagedAudio | null;
  onAddAudio: () => void;
  /** Optional slot rendered to the left of the Add Audio button */
  extraControls?: React.ReactNode;
  /** Hide the clip label (show duration only) */
  hideLabel?: boolean;
}

export function PreviewBar({ staged, onAddAudio, extraControls, hideLabel }: Props) {
  const { t } = useTranslation('audioWorkspace');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const [playing, setPlaying] = useState(false);

  // Draw waveform when staged audio changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !staged) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, w, h);

    const peaks = staged.peaks;
    const barCount = Math.min(peaks.length, w);
    const step = peaks.length / barCount;
    const mid = h / 2;

    ctx.fillStyle = 'rgba(224, 64, 64, 0.6)';
    for (let i = 0; i < barCount; i++) {
      const idx = Math.floor(i * step);
      const amp = peaks[idx] * mid * 0.9;
      const x = (i / barCount) * w;
      const barW = Math.max(1, w / barCount - 0.5);
      ctx.fillRect(x, mid - amp, barW, amp * 2 || 1);
    }
  }, [staged]);

  // Stop playback when staged changes
  useEffect(() => {
    return () => stopPreview();
  }, [staged]);

  const stopPreview = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* already stopped */ }
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    setPlaying(false);
  }, []);

  // Listen for Unity preview state changes in embedded mode
  useEffect(() => {
    if (!capabilities.embeddedRecording) return;

    (window as unknown as Record<string, unknown>).__onPreviewStateChange = (state: string) => {
      if (state === 'stopped' || state === 'error') setPlaying(false);
      else if (state === 'playing') setPlaying(true);
    };

    return () => {
      if (capabilities.embeddedRecording) {
        sendToNative('stopPreviewAudio', {});
        delete (window as unknown as Record<string, unknown>).__onPreviewStateChange;
      }
    };
  }, []);

  const togglePreview = useCallback(async () => {
    if (playing) {
      if (capabilities.embeddedRecording && staged?.clipId != null) {
        sendToNative('stopPreviewAudio', {});
      } else {
        stopPreview();
      }
      setPlaying(false);
      return;
    }

    if (!staged) return;

    // Embedded mode: play via Unity bridge
    if (capabilities.embeddedRecording && staged.clipId != null) {
      sendToNative('playPreviewClip', { clipId: staged.clipId });
      setPlaying(true);
      return;
    }

    // Standalone mode: Web Audio API
    if (!staged.audioBuffer) return;

    const ctx = await ensureAudioContextResumed();
    const source = ctx.createBufferSource();
    source.buffer = staged.audioBuffer;
    source.connect(ctx.destination);
    source.onended = () => {
      sourceRef.current = null;
      setPlaying(false);
    };
    source.start();
    sourceRef.current = source;
    setPlaying(true);
  }, [playing, staged, stopPreview]);

  const formatDuration = (d: number) => d.toFixed(1) + 's';

  if (!staged) {
    return (
      <div className={styles.previewBar}>
        <span className={styles.previewEmpty}>{t('previewEmptyHint')}</span>
      </div>
    );
  }

  return (
    <div className={styles.previewBar}>
      <button className={styles.previewPlayBtn} onClick={togglePreview} title={playing ? t('previewStopTitle') : t('previewPlayTitle')}>
        {playing ? '\u23F9' : '\u25B6'}
      </button>
      <canvas ref={canvasRef} className={styles.waveformCanvas} />
      <span className={styles.previewDuration}>{formatDuration(staged.duration)}</span>
      {!hideLabel && <span className={styles.previewLabel} title={staged.label}>{staged.label}</span>}
      {extraControls}
      <button className={styles.addAudioBtn} onClick={onAddAudio}>
        {t('addAudioButton')}
      </button>
    </div>
  );
}
